import React, { useEffect, useContext, Fragment, /*FormEvent*/ } from "react";
import { useState } from "react";
import { Card, Button, Row, Col, Form, Tooltip, OverlayTrigger } from "react-bootstrap";
import { abpnClient } from "../../../Services/AbpnClient";
import { AuthContext } from "../../Auth/AuthProvider";
import { Address } from "../../Physician/Models/Address";
import AddressValidation from "./AddressValidation";
import { AddressVerificationResult } from "../../Physician/Models/AddressVerificationResult";
import { AddressVerificationStatus } from "../Enums/AddressVerificationStatus";
import { deepCopy } from "../Utility/DeepCopy";
import { CountryLookupItem, StateWithCountryLookupItem } from "../Models/StateWithCountryLookupItem";
import { AddressType } from "../Enums/AddressType";
import { CountryId } from "../Enums/CountryId";
import AbpnSpinner from "./AbpnSpinner";

interface IProps {
    address: Address;
    states: Array<StateWithCountryLookupItem>;
    countries: Array<CountryLookupItem>;
    runValidation: boolean;
    onSubmit: (address: Address) => void;
    onUpdate?: () => void;
    onCancel?: () => void;
    showSubmit?: boolean;
    submitText?: string;
    showCancel?: boolean;
    isLoading?: boolean;
    showStreetAddress?: boolean;
}

export function AddressForm(props: IProps) {
    interface IData {
        addressVerificationResult: AddressVerificationResult,
        address: Address,
        validated: boolean,
        showAddressValidation: boolean,
        showStreetAddress: boolean
    }

    const [data, setData] = useState<IData>({
        addressVerificationResult: null,
        address: props.address,
        validated: false,
        showAddressValidation: false,
        showStreetAddress: props.showStreetAddress ?? true
    });

    const authContext = useContext(AuthContext);

    useEffect(() => {
        if (props.runValidation) validate();
    }, [props.runValidation]);

    useEffect(() => {
        setData(prevData =>
        ({
            ...prevData,
            showAddressValidation: (!prevData.address?.verificationStatus &&
                prevData.addressVerificationResult !== null)
        }));
    }, [data.address?.verificationStatus, data.addressVerificationResult]);

    const validate = () => {
        var isValid = true;
        var updatedData = deepCopy(data);
        const form = document.getElementById("addressForm") as HTMLFormElement;

        if (!form.checkValidity()) isValid = false;

        updatedData.address.isValid = isValid;
        updatedData.validated = true;
        setData(updatedData);

        if (isValid) {
            runMelissaValidation();
        }
    }

    const updateLine1 = (value: string) => {
        var address = data.address;
        if(address == null)
            address = new Address();
        address.line1 = value;
        address.verificationStatus = null;
        setData(prevData => ({ ...prevData, address: address, addressVerificationResult: null }));
        if (props.onUpdate) props.onUpdate();
    }

    const updateLine2 = (value: string) => {
        var address = deepCopy(data.address);
        if(address == null)
            address = new Address();
        address.line2 = value;
        address.verificationStatus = null;
        setData(prevData => ({ ...prevData, address: address, addressVerificationResult: null }));
        if (props.onUpdate) props.onUpdate();
    }

    const updateCity = (value: string) => {
        var address = deepCopy(data.address);
        if(address == null)
            address = new Address();
        address.city = value;
        address.verificationStatus = null;
        setData(prevData => ({ ...prevData, address: address, addressVerificationResult: null }));
        if (props.onUpdate) props.onUpdate();
    }

    const updateZipCode = (value: string) => {
        var address = deepCopy(data.address);
        if(address == null)
            address = new Address();
        address.zipCode = value;
        address.verificationStatus = null;
        setData(prevData => ({ ...prevData, address: address, addressVerificationResult: null }));
        if (props.onUpdate) props.onUpdate();
    }

    const updateState = (e: React.ChangeEvent<HTMLSelectElement>) => {
        var address = deepCopy(data.address);
        if(address == null)
            address = new Address();

        //Handles finding state if selected manually or with autofill
        var state = props.states.find(_ => _.id === parseInt(e.target.value));
        if (!state) state = props.states.find(_ => _.name === e.target.value);
        var value = state ? state.id : null;

        address.stateId = value;
        address.verificationStatus = null;
        setData(prevData => ({ ...prevData, address: address, addressVerificationResult: null }));
        if (props.onUpdate) props.onUpdate();
    }

    const filterStates = (countryId: number) => {
        if (countryId == null) return props.states;
        return props.states.filter(x => x.countryId === countryId)
    }

    const updateCountry = (e: React.ChangeEvent<HTMLSelectElement>) => {
        var address = deepCopy(data.address);
        if(address == null)
            address = new Address();

        //Handles finding country if selected manually or with autofill
        var country = props.countries.find(_ => _.id === parseInt(e.target.value));
        if (!country) {
            country = props.countries.find(_ => _.name === e.target.value);
        }
        address.countryId = country ? country.id : null;

        if (address.countryId) {
            var states = filterStates(address.countryId);
            if (states.length === 1) {
                address.stateId = states[0].id;
            }
        }

        address.verificationStatus = null;
        setData(prevData => ({ ...prevData, address: address, addressVerificationResult: null }));
        if (props.onUpdate) props.onUpdate();
    }

    const updatePhoneNumber = (value: string) => {
        var address = deepCopy(data.address);
        if(address == null)
            address = new Address();

        address.homePhoneNumber = value;
        address.verificationStatus = null;
        setData(prevData => ({ ...prevData, address: address }));
        if (props.onUpdate) props.onUpdate();
    }

    const countryRequiresValidation = (countryId: number) => {
        return countryId === CountryId.Usa || countryId === CountryId.Canada;
    }

    const verifyAndSaveAddress = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        //Validate required fields
        e.currentTarget.classList.add('was-validated');
        if (e.currentTarget.checkValidity()) {
            e.currentTarget.classList.remove('was-validated');

            runMelissaValidation();
        }
    }

    const runMelissaValidation = () => {
        let updatedData = deepCopy(data);

        //determine if address can use Melissa validation
        if (!countryRequiresValidation(updatedData.address.countryId) ||
            (!updatedData.address.isContact &&
                updatedData.address.types.includes(AddressType.Public) &&
                !updatedData.address.types.includes(AddressType.Shipping))) {
            updatedData.address.verificationStatus = AddressVerificationStatus.UseAsEntered;
        }

        if (updatedData.address.verificationStatus) {
            updatedData.address.isValid = true;
            if (updatedData.address.verificationStatus === AddressVerificationStatus.UseSuggested && data.addressVerificationResult !== null) {
                updatedData.address = updateWithSuggestedAddress(updatedData.address);
            }

            props.onSubmit(updatedData.address);
            return;
        }

        //Melissa validation
        abpnClient.post<Array<AddressVerificationResult>>("/api/Address/Verify", [updatedData.address], authContext)
            .then(addressVerificationResults => {

                let validationResult = addressVerificationResults.find(_ => !_.isValid || _.suggestedAddress !== null);

                if (addressVerificationResults.some(_ => _.isValid && !_.suggestedAddress)) {
                    updatedData.address.verificationStatus = AddressVerificationStatus.UseSuggested;
                };
                updatedData.addressVerificationResult = validationResult ?? null;

                if (!updatedData.addressVerificationResult) {
                    updatedData.address.isValid = true;
                    props.onSubmit(updatedData.address);
                }

                setData(updatedData);
            });
    }

    const updateWithSuggestedAddress = (address: Address) => {
        let suggestedAddress = data.addressVerificationResult.suggestedAddress;

        if (suggestedAddress === null) return address;

        address.line1 = suggestedAddress.line1;
        address.line2 = suggestedAddress.line2;
        address.city = suggestedAddress.city;
        address.stateId = suggestedAddress.stateId;
        address.zipCode = suggestedAddress.zipCode;

        return address;
    }

    const onVerificationSelection = (verficationSelection: AddressVerificationStatus) => {
        let updatedAddress = deepCopy(data.address);
        updatedAddress.verificationStatus = verficationSelection;

        updatedAddress.isValid = true;
        if (updatedAddress.verificationStatus === AddressVerificationStatus.UseSuggested && data.addressVerificationResult !== null) {
            updatedAddress = updateWithSuggestedAddress(updatedAddress);
        }

        setData(prevData => ({ ...prevData, address: updatedAddress }));
        if (props.onUpdate) props.onUpdate();
    }

    return (
        <Form noValidate id="addressForm" key={JSON.stringify(data.addressVerificationResult)} onSubmit={(e) => verifyAndSaveAddress(e)} validated={data.validated}>
            <Row>
                {data.showStreetAddress &&
                    <Fragment>
                        <Col xs={12}>
                            <Form.Group controlId="ContactLine1" className="mt-4">
                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Address
                                    <span className="text-pp-primary ml-2">*</span></Form.Label>
                                <Form.Control type="input" required placeholder="123 Main Street"
                                    onChange={(e) => updateLine1(e.target.value)}
                                    defaultValue={data.address?.line1 ? data.address.line1 : null}
                                    value={data.address?.line1} />
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <Form.Group controlId="ContactLine2" className="mt-4">
                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Address 2</Form.Label>
                                <Form.Control type="input" placeholder="Unit A"
                                    onChange={(e) => updateLine2(e.target.value)}
                                    defaultValue={data.address?.line2 ? data.address.line2 : null}
                                    value={data.address?.line2} />
                            </Form.Group>
                        </Col>
                    </Fragment>
                }
                <Col xs={12}>
                    <Form.Group controlId="ContactCountry" className="mt-4">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Country
                            <span className="text-pp-primary ml-2">*</span></Form.Label>
                        <Form.Control as="select" required
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { updateCountry(e); }}
                            defaultValue={data.address?.countryId ? data.address.countryId : ""}
                            value={data.address?.countryId}>
                            <option hidden value="">Select a country...</option>
                            {props.countries.map(country => {
                                return <option key={country.id} value={country.id} >{country.name}</option>
                            })}
                        </Form.Control>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <Form.Group controlId="ContactCity" className="mt-4">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">City
                            <span className="text-pp-primary ml-2">*</span></Form.Label>
                        <Form.Control type="input" required placeholder="Chicago"
                            onChange={(e) => updateCity(e.target.value)}
                            defaultValue={data.address?.city ? data.address.city : null}
                            value={data.address?.city} />
                    </Form.Group>
                </Col>

                {filterStates(data.address?.countryId).length !== 1 &&
                    <Col xs={12}>
                        <Form.Group controlId="ContactState" className="mt-4">
                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">State/Area
                                <span className="text-pp-primary ml-2">*</span></Form.Label>
                            <Form.Control as="select" required
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { updateState(e); }}
                                defaultValue={data.address?.stateId ? data.address.stateId : ""}
                                value={data.address?.stateId}>
                                <option hidden value="">Select a state...</option>
                                {filterStates(data.address?.countryId).map(x => (<option value={x.id} key={x.id}>{x.name}</option>))}
                            </Form.Control>
                        </Form.Group>
                    </Col>}

                <Col xs={12}>
                    <Form.Group controlId="ContactZipCode" className="mt-4">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Postal Code
                            <span className="text-pp-primary ml-2">*</span></Form.Label>
                        <Form.Control
                            type="input"
                            required
                            onChange={(e) => updateZipCode(e.target.value)}
                            defaultValue={data.address?.zipCode ? data.address.zipCode : null}
                            value={data.address?.zipCode}
                            placeholder="00000"
                            name="zipCode" />
                    </Form.Group>
                </Col>
            </Row>
            {data.showStreetAddress &&
                <Row>
                    <Col xs={12}>
                        <Form.Group className="pp-align-self-end mt-4" controlId="ContactPhoneNumber">
                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Phone
                                <span className="text-pp-primary ml-2">*</span>
                                <OverlayTrigger
                                    placement={"right"}
                                    overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                        If outside the US please enter phone number in international format.
                                    </Tooltip>}>
                                    <i className="las la-info-circle ml-1 la-lg"></i>
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control type="input"
                                placeholder="000-000-0000"
                                required
                                onChange={(e) => updatePhoneNumber(e.target.value)}
                                defaultValue={data.address?.homePhoneNumber ? data.address.homePhoneNumber : null}
                                value={data.address?.homePhoneNumber} />
                        </Form.Group>
                    </Col>
                </Row>
            }
            <Card bsPrefix="border-0 pp-align-self-end mt-2">
                {data.showAddressValidation ?
                    <div className="mb-2">
                        <AddressValidation data={data.addressVerificationResult}
                            onUseAsEntered={() => onVerificationSelection(AddressVerificationStatus.UseAsEntered)}
                            onUseSuggested={() => onVerificationSelection(AddressVerificationStatus.UseSuggested)} />
                    </div>
                    :
                    (props.showSubmit ?? true) &&
                    (props.isLoading ?
                        <AbpnSpinner /> :
                        <Button type="submit" variant="pp-primary" className="mr-4 font-weight-bold">{props.submitText ?? 'Continue'}</Button>)
                }
                {props.showCancel && <Button className="font-weight-bold" variant="outline-dark" onClick={props.onCancel}>Cancel</Button>}
            </Card>
        </Form>
    );
}

export default AddressForm;
